import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
} from 'framer-motion';
import {
  Globe, Zap, Shield, Users, Star, ArrowRight, Check, TrendingUp,
  Sparkles, BarChart3, Target, Rocket, Heart, Play,
  MessageCircle, Share2, ThumbsUp, Eye, CheckCircle,
  HeadphonesIcon, ArrowUp, ChevronDown, Music2,
  DollarSign, Crown, X, ChevronLeft, ChevronRight, GraduationCap,
} from 'lucide-react';

import demoImg1 from '../assets/modals/profile.png';
import demoImg2 from '../assets/modals/new-account.png';
import demoImg3 from '../assets/modals/profile-1k.png';
import demoImg4 from '../assets/modals/creativity-program.png';
import demoImg5 from '../assets/modals/account-region.png';

const demoSlides = [
  { image: demoImg1, title: 'UK Profile with 10.7K Followers', desc: 'Your foreign account comes with real followers and engagement.' },
  { image: demoImg2, title: 'Fresh New Account', desc: 'Start fresh with a brand new account ready for your content.' },
  { image: demoImg3, title: 'Growing Profile', desc: 'Watch your audience grow with foreign algorithm advantages.' },
  { image: demoImg4, title: 'Creativity Program Beta', desc: 'Unlock monetization features like the Creativity Program.' },
  { image: demoImg5, title: 'Verified UK Region', desc: 'Your account is registered in a foreign region from day one.' },
];

const heroSlides = [
  { headline: ['Get', 'Your', 'Foreign', 'TikTok', 'Account'], subtitle: 'Unlock global reach with verified accounts from 50+ countries.' },
  { headline: ['Unlock', 'Regional', 'Features'], subtitle: 'Access monetization tools and exclusive features only available in specific regions.' },
  { headline: ['Grow', 'Your', 'Audience', '10x'], subtitle: 'Leverage algorithm advantages from foreign markets to multiply engagement.' },
  { headline: ['Join', '500+', 'Creators', 'Worldwide'], subtitle: 'Be part of a thriving community of creators who have already gone global.' },
];

const stats = [
  { label: 'Accounts Created', value: 500, suffix: '+', icon: Users },
  { label: 'Countries Served', value: 50, suffix: '+', icon: Globe },
  { label: 'Success Rate', value: 99, suffix: '%', icon: CheckCircle },
  { label: 'Support Available', value: 24, suffix: '/7', icon: HeadphonesIcon },
];

const benefits = [
  { icon: TrendingUp, title: 'Higher Engagement', description: 'Foreign accounts see 3-5x more engagement due to algorithm advantages.' },
  { icon: Globe, title: 'Better Algorithm Reach', description: 'Access TikTok algorithms designed for different regions.' },
  { icon: Sparkles, title: 'Regional Features', description: 'Unlock features only available in specific countries.' },
  { icon: DollarSign, title: 'Monetization', description: 'Access creator funds and monetization from multiple regions.' },
  { icon: Shield, title: 'Brand Safety', description: 'Protect your brand with accounts from trusted regions.' },
  { icon: Users, title: 'Global Audience', description: 'Reach audiences worldwide and expand distribution.' },
];

const steps = [
  { number: '01', title: 'Choose Package', description: 'Select Create Foreign TikTok or Learn How To Create based on your needs.', icon: Target },
  { number: '02', title: 'Fill Details', description: 'Complete our simple multi-step form.', icon: BarChart3 },
  { number: '03', title: 'Get Account', description: 'We create and deliver your ready-to-use account.', icon: Rocket },
];

const testimonials = [
  { name: 'Sarah Johnson', country: 'United States', rating: 5, text: 'Got my UK TikTok account in less than 24 hours. The engagement difference is incredible!', avatar: 'SJ' },
  { name: 'Marco Rodriguez', country: 'Spain', rating: 5, text: 'The Create Foreign TikTok package was worth every penny. Custom bio and growth guide helped me gain 50k followers.', avatar: 'MR' },
  { name: 'Yuki Tanaka', country: 'Japan', rating: 5, text: 'Best investment for my content career. The regional features have been game-changing.', avatar: 'YT' },
];

const AnimatedCounter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const duration = 2000;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [end, started]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const PhoneMockup = ({ slideIndex }) => {
  const socialIcons = [
    { icon: Heart, color: '#FF2D55', label: '12.4K' },
    { icon: MessageCircle, color: '#00F2EA', label: '856' },
    { icon: Share2, color: '#FF2D55', label: '2.1K' },
    { icon: ThumbsUp, color: '#00F2EA', label: '34.2K' },
    { icon: Eye, color: '#FF2D55', label: '1.8M' },
  ];
  const screenColors = [
    'from-gray-800 via-gray-900 to-black',
    'from-gray-900 via-blue-900/20 to-black',
    'from-gray-900 via-pink-900/20 to-black',
    'from-gray-900 via-green-900/20 to-black',
  ];
  return (
    <div className="relative mx-auto" style={{ perspective: '1000px' }}>
      <motion.div animate={{ rotateY: [-5, 5, -5] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }} style={{ transformStyle: 'preserve-3d' }}>
        <div className="relative w-[260px] h-[520px] bg-black rounded-[40px] border-[3px] border-gray-700 overflow-hidden shadow-2xl shadow-primary-500/20">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-black rounded-b-2xl z-20" />
          <AnimatePresence mode="wait">
            <motion.div key={slideIndex} initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.6 }} className={`absolute inset-0 bg-gradient-to-b ${screenColors[slideIndex]}`}>
              <div className="absolute top-12 left-4 right-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-accent-400" />
                  <div className="text-white text-xs font-semibold">trending_creator_{slideIndex + 1}</div>
                </div>
                <div className="text-white/80 text-xs mb-1">trending sound</div>
                <div className="text-white text-sm font-medium mb-3">Check out this amazing content! #fyp #viral</div>
                <div className="flex gap-1.5 flex-wrap">
                  {['#foryou', '#viral', '#trending', '#fyp'].map((tag) => (
                    <span key={tag} className="text-primary-400 text-[10px] font-medium">{tag}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="absolute right-3 bottom-24 flex flex-col items-center gap-4 z-10">
            {socialIcons.map((item, i) => (
              <motion.div key={i} className="flex flex-col items-center" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.15 }}>
                <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}>
                  <item.icon size={22} style={{ color: item.color }} />
                </motion.div>
                <span className="text-white text-[9px] mt-0.5">{item.label}</span>
              </motion.div>
            ))}
          </div>
          <div className="absolute bottom-4 inset-x-0 flex justify-center">
            <motion.div animate={{ width: ['40%', '60%', '40%'] }} transition={{ duration: 3, repeat: Infinity }} className="h-1 bg-white/30 rounded-full" />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Landing = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const gradientX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const gradientY = useSpring(mouseY, { stiffness: 50, damping: 20 });
  const [gradPos, setGradPos] = useState({ x: 0, y: 0 });
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoIndex, setDemoIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setSlideIndex((p) => (p + 1) % heroSlides.length), 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const unsubX = gradientX.on('change', (v) => setGradPos((p) => ({ ...p, x: v })));
    const unsubY = gradientY.on('change', (v) => setGradPos((p) => ({ ...p, y: v })));
    return () => { unsubX(); unsubY(); };
  }, [gradientX, gradientY]);

  useEffect(() => {
    const handleMouse = (e) => { mouseX.set(e.clientX); mouseY.set(e.clientY); };
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, [mouseX, mouseY]);

  const currentSlide = heroSlides[slideIndex];

  const nextDemo = useCallback(() => setDemoIndex((p) => (p + 1) % demoSlides.length), []);
  const prevDemo = useCallback(() => setDemoIndex((p) => (p - 1 + demoSlides.length) % demoSlides.length), []);
  const demoSlide = demoSlides[demoIndex];

  return (
    <div className="min-h-screen bg-gray-950 overflow-x-hidden">
      {/* Demo Modal */}
      <AnimatePresence>
        {demoOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/80 backdrop-blur-md"
            onClick={() => setDemoOpen(false)}>
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-gray-900 border border-gray-800 sm:rounded-3xl rounded-t-3xl overflow-hidden max-w-4xl w-full max-h-[90vh] shadow-2xl"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
                <div>
                  <h3 className="text-white font-semibold">Account Showcase</h3>
                  <p className="text-xs text-gray-500">Slide {demoIndex + 1} of {demoSlides.length}</p>
                </div>
                <button onClick={() => setDemoOpen(false)} className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                  <X size={16} />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[calc(90vh-130px)]">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="flex items-center justify-center p-6 sm:p-8 bg-gradient-to-br from-gray-900 to-gray-800">
                    <motion.div key={demoIndex} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}
                      transition={{ duration: 0.3 }}
                      className="w-full flex items-center justify-center">
                      <img src={demoSlide.image} alt={demoSlide.title}
                        className="w-full max-w-[280px] h-auto max-h-[400px] object-contain rounded-2xl shadow-2xl border border-gray-700" />
                    </motion.div>
                  </div>
                  <div className="p-6 sm:p-8 flex flex-col justify-center">
                    <motion.div key={`info-${demoIndex}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-6">
                      <div>
                        <h4 className="text-xl sm:text-2xl font-bold text-white mb-2">{demoSlide.title}</h4>
                        <p className="text-gray-400 text-sm">{demoSlide.desc}</p>
                      </div>
                      <div className="bg-gradient-to-r from-primary-500/10 to-accent-500/10 border border-primary-500/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Crown size={16} className="text-primary-400" />
                          <span className="text-white font-semibold text-sm">What You Get</span>
                        </div>
                        <ul className="space-y-1.5">
                          {['Full regional features unlocked', 'Monetization ready', 'Custom bio & profile setup', '24/7 support included'].map((f, i) => (
                            <li key={i} className="flex items-center gap-2 text-xs text-gray-300">
                              <Check size={12} className="text-green-400" /> {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Link to="/create-request" className="block">
                        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                          className="w-full py-4 bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg shadow-primary-500/25">
                          Get Your Account <ArrowRight size={20} />
                        </motion.button>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800 bg-gray-900/50 sticky bottom-0">
                <button onClick={prevDemo} className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <div className="flex gap-2">
                  {demoSlides.map((_, i) => (
                    <button key={i} onClick={() => setDemoIndex(i)}
                      className={`h-2 rounded-full transition-all ${i === demoIndex ? 'w-8 bg-primary-500' : 'w-2 bg-gray-700 hover:bg-gray-600'}`} />
                  ))}
                </div>
                <button onClick={nextDemo} className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                  <ChevronRight size={20} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="pointer-events-none fixed inset-0 z-50 hidden lg:block" style={{ background: `radial-gradient(600px circle at ${gradPos.x}px ${gradPos.y}px, rgba(139,92,246,0.08), transparent 50%)` }} />

      {/* ==================== HERO ==================== */}
      <section className="relative min-h-screen flex items-center pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20">
                <Zap size={14} className="text-primary-400" />
                <span className="text-sm text-primary-300">Trusted by 500+ creators worldwide</span>
              </motion.div>
              <div className="space-y-2">
                <AnimatePresence mode="wait">
                  <motion.div key={slideIndex} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }} transition={{ duration: 0.5 }}>
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                      {currentSlide.headline.map((word, i) => (
                        <motion.span key={`${slideIndex}-${i}`} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
                          className={i >= currentSlide.headline.length - 2 ? 'bg-gradient-to-r from-primary-400 via-accent-400 to-pink-400 bg-clip-text text-transparent' : 'text-white'}>
                          {word}{' '}
                        </motion.span>
                      ))}
                    </h1>
                    <p className="text-lg text-gray-400 max-w-lg mt-4">{currentSlide.subtitle}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/create-request">
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="px-8 py-4 bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl text-white font-semibold text-lg flex items-center gap-2 shadow-lg shadow-primary-500/25">
                    Create Now <ArrowRight size={20} />
                  </motion.button>
                </Link>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={() => { setDemoIndex(0); setDemoOpen(true); }}
                  className="px-8 py-4 border border-gray-700 rounded-xl text-white font-semibold text-lg flex items-center gap-2 hover:bg-gray-800/50">
                  <Play size={20} /> Watch Demo
                </motion.button>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3">
                  {['bg-gradient-to-br from-blue-400 to-blue-600', 'bg-gradient-to-br from-green-400 to-green-600', 'bg-gradient-to-br from-purple-400 to-purple-600', 'bg-gradient-to-br from-pink-400 to-pink-600'].map((c, i) => (
                    <div key={i} className={`w-10 h-10 rounded-full ${c} border-2 border-gray-950 flex items-center justify-center text-white text-xs font-bold`}>
                      {['S', 'M', 'Y', 'A'][i]}
                    </div>
                  ))}
                </div>
                <div className="text-sm text-gray-400">
                  <span className="text-white font-semibold">500+</span> creators trust Advance Advert Tv Digital
                </div>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <PhoneMockup slideIndex={slideIndex} />
            </div>
          </div>
        </div>
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <ChevronDown size={24} className="text-gray-500" />
        </motion.div>
      </section>

      {/* ==================== STATS ==================== */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 via-accent-500/5 to-primary-500/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center mb-16">
            <motion.div initial={{ scale: 0.5, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 200, damping: 15 }} className="inline-block mb-6">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/30">
                <BarChart3 size={32} className="text-white" />
              </div>
            </motion.div>
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="text-4xl md:text-5xl font-bold text-white mb-4">
              Numbers That <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">Speak</span>
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.25 }} className="text-gray-400 text-lg">Our track record speaks for itself</motion.p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <motion.div key={stat.label}
                initial={{ opacity: 0, y: 60, scale: 0.8, rotateX: 20 }}
                whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, type: 'spring', stiffness: 150, damping: 12 }}
                whileHover={{ y: -12, scale: 1.05, boxShadow: '0 20px 40px rgba(139,92,246,0.15)' }}
                className="text-center p-8 rounded-2xl bg-gray-900/60 border border-gray-800/60 group cursor-default">
                <motion.div whileHover={{ rotate: 360, scale: 1.3 }} transition={{ duration: 0.5 }}>
                  <stat.icon size={32} className="mx-auto mb-4 text-primary-400 group-hover:text-accent-400 transition-colors" />
                </motion.div>
                <div className="text-4xl md:text-5xl font-bold text-white mb-2"><AnimatedCounter end={stat.value} suffix={stat.suffix} /></div>
                <div className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== BENEFITS ==================== */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center mb-16">
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 120 }} className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Foreign <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">TikTok</span>?
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="text-gray-400 text-lg max-w-2xl mx-auto">
              Unlock advantages that domestic accounts simply cannot offer
            </motion.p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <motion.div key={b.title}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, type: 'spring', stiffness: 120, damping: 14 }}
                whileHover={{ y: -14, scale: 1.03, boxShadow: '0 25px 50px rgba(139,92,246,0.12)' }}
                className="p-8 rounded-2xl bg-gray-900/60 border border-gray-800/60 group cursor-default">
                <motion.div whileHover={{ rotate: [0, -10, 10, 0], scale: 1.2 }} transition={{ duration: 0.4 }}
                  className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center mb-6 group-hover:from-primary-500/30 group-hover:to-accent-500/30 transition-all">
                  <b.icon size={28} className="text-primary-400 group-hover:text-accent-400 transition-colors" />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-3">{b.title}</h3>
                <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">{b.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="relative py-32 bg-gray-900/20 overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-accent-500/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center mb-16">
            <motion.div initial={{ scale: 0, rotate: -180 }} whileInView={{ scale: 1, rotate: 0 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 200, damping: 12 }} className="inline-block mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/30">
                <Rocket size={32} className="text-white" />
              </div>
            </motion.div>
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="text-4xl md:text-5xl font-bold text-white mb-4">
              How It <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">Works</span>
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.25 }} className="text-gray-400 text-lg">Three simple steps to go global</motion.p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.8 }}
              className="hidden md:block absolute top-1/2 left-[16%] right-[16%] h-1 bg-gradient-to-r from-primary-500 via-accent-500 to-primary-500 rounded-full -translate-y-1/2 origin-left" />
            {steps.map((step, i) => (
              <motion.div key={step.number}
                initial={{ opacity: 0, y: 60, scale: 0.8 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.2, type: 'spring', stiffness: 100, damping: 12 }}
                className="relative text-center">
                <motion.div whileHover={{ scale: 1.15, rotate: 360 }} transition={{ duration: 0.6 }}
                  className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/20 relative">
                  <step.icon size={32} className="text-white" />
                  <motion.div animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0, 0.3] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.5 }}
                    className="absolute inset-0 rounded-2xl border-2 border-primary-400/30" />
                </motion.div>
                <motion.div initial={{ width: 0 }} whileInView={{ width: 'auto' }} viewport={{ once: true }} transition={{ delay: 0.4 + i * 0.2 }}
                  className="overflow-hidden inline-block">
                  <div className="text-sm text-primary-400 font-bold mb-2 whitespace-nowrap">STEP {step.number}</div>
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PRICING ==================== */}
      <section id="pricing" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center mb-16">
            <motion.div initial={{ scale: 0, rotate: -180 }} whileInView={{ scale: 1, rotate: 0 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 200, damping: 12 }} className="inline-block mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/30">
                <Sparkles size={32} className="text-white" />
              </div>
            </motion.div>
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="text-4xl md:text-5xl font-bold text-white mb-4">
              Choose Your <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">Plan</span>
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.25 }} className="text-gray-400 text-lg">One-time payment. No subscriptions.</motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Account Package */}
            <motion.div initial={{ opacity: 0, y: 60, scale: 0.9 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 100, damping: 14 }}
              className="relative group">
              <motion.div animate={{ rotate: [0, 1, -1, 0] }} transition={{ duration: 4, repeat: Infinity }} className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-accent-500 to-primary-500 rounded-3xl opacity-20 group-hover:opacity-40 blur transition-opacity" />
              <div className="relative p-8 rounded-3xl bg-gradient-to-b from-primary-500/10 to-accent-500/10 border-2 border-primary-500/40 backdrop-blur-sm h-full flex flex-col">
                <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity }} className="mb-4">
                  <Crown className="text-accent-400 mx-auto" size={36} />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-2 text-center">Create Foreign TikTok</h3>
                <div className="text-center mb-6">
                  <span className="text-5xl font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">₦5,000</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {['Foreign TikTok Account', 'Any Country Choice', 'Priority 24/7 Support', 'Custom Bio & Branding', 'Growth Strategy Guide'].map((f, i) => (
                    <motion.li key={f} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.08 }}
                      className="flex items-center gap-3 text-gray-300 text-sm">
                      <Check size={18} className="text-green-400 flex-shrink-0" />
                      {f}
                    </motion.li>
                  ))}
                </ul>
                <Link to="/create-request">
                  <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white font-bold text-lg shadow-lg shadow-primary-500/25 relative overflow-hidden">
                    <span className="relative flex items-center justify-center gap-2">Create Now <ArrowRight size={20} /></span>
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            {/* Learning Package */}
            <motion.div initial={{ opacity: 0, y: 60, scale: 0.9 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 100, damping: 14, delay: 0.1 }}
              className="relative group">
              <motion.div animate={{ rotate: [0, 1, -1, 0] }} transition={{ duration: 4, repeat: Infinity }} className="absolute -inset-1 bg-gradient-to-r from-accent-500 via-yellow-500 to-accent-500 rounded-3xl opacity-20 group-hover:opacity-40 blur transition-opacity" />
              <div className="relative p-8 rounded-3xl bg-gradient-to-b from-accent-500/10 to-yellow-500/10 border-2 border-accent-500/40 backdrop-blur-sm h-full flex flex-col">
                <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity }} className="mb-4">
                  <GraduationCap className="text-yellow-400 mx-auto" size={36} />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-2 text-center">Learn How To Create</h3>
                <div className="text-center mb-6">
                  <span className="text-5xl font-bold bg-gradient-to-r from-accent-400 to-yellow-400 bg-clip-text text-transparent">₦10,000</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {['Complete TikTok Growth Course', 'Monetization Strategies', 'Content Creation Masterclass', 'Algorithm Hacks & Viral Tips', 'Lifetime Access to Updates', 'Priority 30-Day Support'].map((f, i) => (
                    <motion.li key={f} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.08 }}
                      className="flex items-center gap-3 text-gray-300 text-sm">
                      <Check size={18} className="text-green-400 flex-shrink-0" />
                      {f}
                    </motion.li>
                  ))}
                </ul>
                <Link to="/learn">
                  <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-accent-500 to-yellow-500 text-white font-bold text-lg shadow-lg shadow-accent-500/25 relative overflow-hidden">
                    <span className="relative flex items-center justify-center gap-2">Learn Now <ArrowRight size={20} /></span>
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section className="relative py-32 bg-gray-900/20 overflow-hidden">
        <div className="absolute top-20 right-20 w-40 h-40 bg-accent-500/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center mb-16">
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 120 }} className="text-4xl md:text-5xl font-bold text-white mb-4">
              Loved by <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">Creators</span>
            </motion.h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div key={t.name}
                initial={{ opacity: 0, y: 50, rotateY: -15 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, type: 'spring', stiffness: 100, damping: 14 }}
                whileHover={{ y: -12, scale: 1.02, boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}
                className="p-8 rounded-2xl bg-gray-900/60 border border-gray-800/60 group cursor-default relative overflow-hidden">
                <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="absolute -top-10 -right-10 w-20 h-20 bg-primary-500/5 rounded-full group-hover:bg-primary-500/10 transition-colors" />
                <div className="flex gap-1 mb-4 relative z-10">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <motion.div key={j} initial={{ scale: 0, rotate: -180 }} whileInView={{ scale: 1, rotate: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + j * 0.08, type: 'spring', stiffness: 300 }}>
                      <Star size={16} className="text-yellow-400" fill="currentColor" />
                    </motion.div>
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed relative z-10">"{t.text}"</p>
                <div className="flex items-center gap-3 relative z-10">
                  <motion.div whileHover={{ scale: 1.15, rotate: 10 }} className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-white font-bold">
                    {t.avatar}
                  </motion.div>
                  <div>
                    <div className="text-white font-semibold">{t.name}</div>
                    <div className="text-gray-400 text-sm">{t.country}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.05, 0.1, 0.05] }} transition={{ duration: 6, repeat: Infinity }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
          <motion.div animate={{ scale: [1.2, 1, 1.2], opacity: [0.1, 0.05, 0.1] }} transition={{ duration: 6, repeat: Infinity, delay: 3 }}
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ scale: 0, rotate: -180 }} whileInView={{ scale: 1, rotate: 0 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 150, damping: 12 }} className="inline-block mb-10">
            <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 5, repeat: Infinity }}>
              <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-3xl flex items-center justify-center shadow-lg shadow-primary-500/30">
                <Globe size={44} className="text-white" />
              </div>
            </motion.div>
          </motion.div>
          <motion.h2 initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15, type: 'spring', stiffness: 100 }}
            className="text-5xl md:text-7xl font-bold text-white mb-8">
            Ready to Go <span className="bg-gradient-to-r from-primary-400 via-accent-400 to-pink-400 bg-clip-text text-transparent">Global</span>?
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
            Join 500+ creators who have already unlocked their potential with foreign TikTok accounts.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.9 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.45, type: 'spring', stiffness: 120 }}>
            <Link to="/create-request">
              <motion.button whileHover={{ scale: 1.08, boxShadow: '0 0 60px rgba(139,92,246,0.4)' }} whileTap={{ scale: 0.95 }}
                className="px-12 py-5 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl text-white font-bold text-xl shadow-lg shadow-primary-500/30 relative overflow-hidden">
                <motion.div animate={{ x: ['-100%', '100%'] }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <span className="relative flex items-center gap-3">Start Your Journey <ArrowRight size={24} /></span>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Back to Top */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        whileHover={{ scale: 1.1, y: -2 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30 z-50"
      >
        <ArrowUp size={20} className="text-white" />
      </motion.button>
    </div>
  );
};

export default Landing;
